/**
 * Projection for `MineSiteScene` — the demo's `renderSites(m)` (line 3232).
 *
 * The site page reads six lazy domains plus the lake catalogue, and its seven
 * tabs all draw on the same context. This file assembles that context once from
 * the loaded payloads and exposes one derived view per tab, so the scene stays
 * a render loop.
 *
 * Pure: no React, no fetching.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/sites/MineSiteScene.tsx
 * @see docs/logic/sites/mine-site.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { BY_ID, CONTRACTORS, MINES, RATES } from '../../data/core';
import type { InventoryLine } from '../../schema/part';
import type { MineCode } from '../../schema/machine';
import type { SiteInfo } from '../../schema/mine';
import type { CustomerSatisfactionResponse, RosterEntry } from '../../schema/person';
import type { PartOrder } from '../../schema/part';
import type { TrainingRecord } from '../../schema/service';
import {
  alertCardData,
  dcStore,
  fmt$,
  incidentStrip,
  techStats,
  predictedUsage,
  siteCSS,
  siteDocs,
  siteFleetStrip,
  siteHead,
  siteJobs,
  siteKPIs,
  siteStore,
  siteTechs,
  type CaseCtx,
  type ContractCtx,
  type CssCtx,
  type InventoryCtx,
  type LakeCtx,
  type OrderCtx,
  type PartsCatalogueCtx,
  type PredictedUsageRow,
  type RosterCtx,
  type ShiftCtx,
  type SiteFleetGroup,
  type SiteHead,
  type SiteKpis,
  type TechJobCtx,
  type TechPresenceCtx,
  type TimesheetCtx,
  type TrainingCtx,
  type WorkOrderCtx,
  type LakeIndexEntry,
  type AlertCardData,
} from '../../shared/oem-engine';
import { routeForView } from '../../shared/oem-routes';
import { ALERTS } from '../../shared/seed/alerts';
import {
  channelFor,
  ORG_OF_MINE,
  OEM_ORG,
  oemOrgLabel,
} from '../customers/CustomersScene.transform';

/** Everything the site page's seven tabs read, composed once. */
export type SiteSceneCtx = ShiftCtx &
  CssCtx &
  ContractCtx &
  WorkOrderCtx &
  TechJobCtx &
  CaseCtx &
  InventoryCtx &
  PartsCatalogueCtx &
  OrderCtx &
  TechPresenceCtx &
  RosterCtx &
  TrainingCtx &
  LakeCtx;

/** The header block — the demo's `siteHead` plus the picker's sibling sites. */
export interface SiteBrief {
  readonly head: SiteHead;
  readonly kpis: SiteKpis;
  /** `sites.json` `siteinfo[code]`, or `null` when the profile is missing. */
  readonly info: SiteInfo | null;
  /** Contractor operating the site, or `null` at the owner-operated three. */
  readonly contractor: string | null;
  readonly dataMode: string;
  readonly isLive: boolean;
}

/** Demo line 3206 / 3221. Header and KPI tiles together. */
export function siteBrief(
  mine: MineCode,
  ctx: SiteSceneCtx,
  info: SiteInfo | null,
): SiteBrief {
  const m = MINES[mine];
  return {
    head: siteHead(mine, ctx),
    kpis: siteKPIs(mine, ctx),
    info,
    contractor: CONTRACTORS[mine] ?? null,
    dataMode: m.data_mode,
    isLive: m.data_mode === 'Live',
  };
}

// ---------------------------------------------------------------------------
// Fleet strip
// ---------------------------------------------------------------------------

/** A fleet-strip group with a React key — `working` may be `null`. */
export interface KeyedFleetGroup extends SiteFleetGroup {
  readonly key: string;
  /** `'Spinifex decline · 6'`, or `''` for the unnamed single group. */
  readonly heading: string;
}

/** Demo line 3227. `siteFleetStrip`, keyed and labelled. */
export const fleetStrip = (mine: MineCode): KeyedFleetGroup[] =>
  siteFleetStrip(mine).map((group, i) => ({
    ...group,
    key: group.working ?? `all#${i}`,
    heading: group.working ? `${group.working} · ${group.machines.length}` : '',
  }));

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

/** One row of the People tab's Meridian service team. */
export interface SiteTechRow {
  readonly key: string;
  readonly name: string;
  readonly initials: string;
  readonly skills: string;
  readonly status: string;
  readonly onSite: boolean;
  /** Roster line: home base, swing start and swing end, or `null`. */
  readonly roster: RosterEntry | null;
}

/**
 * Demo line 3244 / 3254. The technicians based at a site, joined to their
 * roster line. The per-technician figures the People tab prints under each
 * name come from {@link siteTechStats}, which needs the timesheets as well.
 */
export function siteTechRows(mine: MineCode, ctx: SiteSceneCtx): SiteTechRow[] {
  return siteTechs(mine, ctx).map((t) => ({
    key: t.technician,
    name: t.technician,
    initials: t.technician
      .split(' ')
      .map((w) => w[0])
      .join(''),
    skills: t.skills.join(' · '),
    status: t.status,
    onSite: t.status === 'On site',
    roster: ctx.roster.find((r) => r.Technician === t.technician) ?? null,
  }));
}

/** Demo line 3254. The figures printed under one technician on the People tab. */
export interface SiteTechStat {
  /** Completed technician-app jobs. */
  readonly done: number;
  /** Fix-first-time %, or `null` when nothing is completed. */
  readonly fft: number | null;
  /** LMS records not `Current` — certificates to renew. */
  readonly toRenew: number;
  /** `'6 jobs · fix-first-time 83%'` — the demo's line, minus the renewals. */
  readonly line: string;
  readonly href: string;
}

/**
 * Demo line 3254. `techStats()` for every technician based at the site, keyed
 * by name. Needs the timesheets (`costs.json`) on top of the page context,
 * which the People tab loads on its own so the rest of the page does not wait.
 */
export function siteTechStats(
  mine: MineCode,
  ctx: SiteSceneCtx & TimesheetCtx,
): Readonly<Record<string, SiteTechStat>> {
  return Object.fromEntries(
    siteTechs(mine, ctx).map((t) => {
      const st = techStats(t.technician, ctx);
      return [
        t.technician,
        {
          done: st.done,
          fft: st.fft,
          toRenew: st.expiring.length,
          line: `${st.done} jobs · fix-first-time ${st.fft == null ? '—' : `${st.fft}%`}`,
          href: routeForView('techs', t.technician) ?? '/technicians',
        },
      ];
    }),
  );
}

/** Demo line 3254. `'2026-09-02'` → `'09-02'`, as the swing dates print. */
export const swingDate = (date: string): string => date.slice(5);

/** One row of the People tab's key-contacts table. */
export interface SiteContactRow {
  readonly key: string;
  readonly name: string;
  readonly role: string;
  /** Display organisation — the profile's `'site'` rows read as the site name. */
  readonly org: string;
  /** Customer page for the contact's organisation; `null` for Meridian's own people. */
  readonly orgHref: string | null;
  /** Email, or `'via the site'` on the rows that carry none. */
  readonly contact: string;
}

/**
 * Demo line 3252. The site profile's key contacts, in profile order.
 *
 * The profile repeats the superintendent and planner as `org: 'site'` rows with
 * no email; the demo prints them, so they are kept. Every customer or
 * contractor contact links to that organisation's customer page — a `'site'`
 * row to the site's owner. The OEM's own people have no customer page, and
 * their organisation is shown as `oemName` — the selected brand's name, since
 * the data spells it "Meridian Equipment" under every brand.
 */
export function siteContactRows(
  mine: MineCode,
  info: SiteInfo,
  oemName: string,
): SiteContactRow[] {
  return info.key_contacts.map((c, i) => {
    const target = c.org === 'site' ? ORG_OF_MINE[mine] : c.org;
    return {
      key: `${i}#${c.name}`,
      name: c.name,
      role: c.role,
      org: c.org === 'site' ? MINES[mine].name : oemOrgLabel(c.org, oemName),
      orgHref: target === OEM_ORG ? null : routeForView('customers', target),
      contact: c.email || 'via the site',
    };
  });
}

/** Demo line 3252. The note under the contacts: the owner's channel and its page. */
export interface SiteCustomer {
  readonly org: string;
  readonly channel: string;
  readonly href: string;
}

/** Demo line 3252. `CUST_CHANNEL[ORG_OF_MINE[m]] || 'Email'`, and the customer page. */
export function siteCustomer(mine: MineCode): SiteCustomer {
  const org = ORG_OF_MINE[mine];
  return { org, channel: channelFor(org), href: routeForView('customers', org) ?? '/customers' };
}

/** One row of the People tab's competency table. */
export interface CompetencyRow {
  readonly key: string;
  readonly record: TrainingRecord;
  /**
   * `band-*` class for the status pill. The demo also mapped `Expired` to
   * very-poor red, but the register's `TrainingStatus` has only `Current` and
   * `Expiring`, so two classes cover every row.
   */
  readonly pill: 'band-good' | 'band-fair';
}

/**
 * Demo line 3256. LMS records for the people at a site — anyone recorded
 * against the site, plus the Meridian technicians based there — capped at 18
 * rows, in register order.
 *
 * **One deliberate correction.** The demo compared `x.site` with the site's
 * display *name*, but the register stores the mine *code*, so its first clause
 * never matched and the table only ever listed the based technicians. The port
 * compares codes, which is what the heading ("Competency on site") promises.
 */
export function siteCompetency(mine: MineCode, ctx: SiteSceneCtx): CompetencyRow[] {
  const techs = new Set(siteTechs(mine, ctx).map((t) => t.technician));
  return ctx.training
    .filter((x) => x.site === mine || techs.has(x.person))
    .slice(0, 18)
    .map((record) => ({
      key: record.lms_id,
      record,
      pill: record.status === 'Current' ? 'band-good' : 'band-fair',
    }));
}

/** Demo line 3251. Customer-satisfaction responses for the site. */
export const siteSurvey = (
  mine: MineCode,
  ctx: SiteSceneCtx,
): readonly CustomerSatisfactionResponse[] => siteCSS(mine, ctx);

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

/** One row of the Parts tab's store table. */
export interface StoreRow {
  readonly key: string;
  readonly line: InventoryLine;
  readonly description: string;
  readonly criticality: string;
  readonly belowMinimum: boolean;
}

/** The Parts tab, as data. */
export interface SiteParts {
  readonly storeLines: number;
  readonly dcLines: number;
  readonly belowMinimum: number;
  readonly dc: string;
  readonly freightDays: number;
  /** Critical or below-minimum lines, shortest cover first, capped at 18. */
  readonly rows: readonly StoreRow[];
  /** Orders raised to this site since 20 Aug 2026 — the demo's window. */
  readonly orders: readonly PartOrder[];
  readonly draftOrders: number;
  readonly airFreightOrders: number;
  readonly predicted: readonly PredictedUsageRow[];
  readonly predictedGaps: number;
}

/** Demo line 3266. `SITE_TAB === 'parts'`. */
export function siteParts(mine: MineCode, ctx: SiteSceneCtx): SiteParts {
  const store = siteStore(mine, ctx);
  const dc = dcStore(mine, ctx);
  const rates = RATES[mine];

  const rows = store
    .map((line) => {
      const part = ctx.parts.find((p) => p.part_no === line.part_no);
      return {
        key: `${line.location}#${line.part_no}`,
        line,
        description: part?.description ?? '',
        criticality: part?.criticality ?? '',
        belowMinimum: line.qty_on_hand < line.min_level,
      };
    })
    .filter((r) => r.belowMinimum || r.criticality === 'Critical')
    .sort(
      (a, b) =>
        a.line.qty_on_hand - a.line.min_level - (b.line.qty_on_hand - b.line.min_level),
    )
    .slice(0, 18);

  // Demo line 3266: orders raised in the last three weeks of the dataset.
  const orders = ctx.orders
    .filter((o) => o.mine === mine && o.order_date >= '2026-08-20')
    .slice()
    .sort((a, b) => b.order_date.localeCompare(a.order_date));

  const predicted = predictedUsage(ctx).filter((r) => r.mine === mine);

  return {
    storeLines: store.length,
    dcLines: dc.length,
    belowMinimum: store.filter((i) => i.qty_on_hand < i.min_level).length,
    dc: rates.dc,
    freightDays: rates.freight_days,
    rows,
    orders: orders.slice(0, 14),
    draftOrders: orders.filter((o) => /Draft/.test(o.status)).length,
    airFreightOrders: orders.filter((o) => /air/i.test(o.status)).length,
    predicted,
    predictedGaps: predicted.filter((r) => r.gap > 0).length,
  };
}

// ---------------------------------------------------------------------------
// Ground, services and access
// ---------------------------------------------------------------------------

/** A labelled block of the Conditions tab. */
export interface ConditionsBlock {
  readonly title: string;
  readonly pairs: readonly (readonly [label: string, value: string])[];
}

/** Demo line 3273. `SITE_TAB === 'conditions'` — six blocks, in source order. */
export function siteConditions(mine: MineCode, info: SiteInfo): ConditionsBlock[] {
  const m = MINES[mine];
  return [
    {
      title: 'Ground conditions',
      pairs: [
        ['Regime', info.ground_conditions.regime],
        ['Support', info.ground_conditions.support],
        ['Seismicity', info.ground_conditions.seismic],
        ['What to watch', info.ground_conditions.watch],
      ],
    },
    {
      title: 'Mine services',
      pairs: [
        ['Ventilation', info.mine_services.ventilation],
        ['Dewatering', info.mine_services.dewatering],
        ['Power', info.mine_services.power],
        ['Water', info.mine_services.water],
        ['Communications', info.mine_services.comms],
      ],
    },
    {
      title: 'Access, roads and logistics',
      pairs: [
        ['Nearest town', info.access_and_logistics.nearest_town],
        ['Air and road', info.access_and_logistics.airport],
        ['Roster', info.access_and_logistics.roster],
        ['Road conditions', info.access_and_logistics.road],
        ['Freight', info.access_and_logistics.freight],
      ],
    },
    {
      title: 'Facilities on site',
      pairs: [
        ['Workshop', info.facilities.workshop],
        ['Store', info.facilities.store],
        ['Fuel', info.facilities.fuel],
        ['Crib and work windows', info.facilities.crib],
      ],
    },
    {
      title: 'Operating model',
      pairs: [
        ['Operator', m.operator_model],
        ['Quality', m.operator_quality],
        ['Contractor', info.contractor ?? 'None — owner-operated'],
        ['Crews', m.crews.join(', ')],
        ['Workings', info.workings.join(', ') || 'Single decline / pit'],
        ['Fleet management system', m.fms],
      ],
    },
    {
      title: 'Energy and cost basis',
      pairs: [
        ['Power source', info.energy.power_source],
        ['Grid factor', `${info.energy.grid_kgco2_per_kwh} kg CO₂-e/kWh`],
        [
          'Diesel',
          info.energy.diesel_aud_per_l ? `A$${info.energy.diesel_aud_per_l}/L` : '—',
        ],
        ['Labour rate', `A$${m.labour_rate}/h`],
        [
          'Lost production',
          Object.entries(m.lost_prod)
            .map(([k, v]) => `${k} A$${v.toLocaleString('en-AU')}/h`)
            .join(' · '),
        ],
      ],
    },
  ];
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** Documents about a site, grouped by source system. */
export interface SiteDocGroup {
  readonly connector: string;
  readonly entries: readonly LakeIndexEntry[];
}

/** Demo line 3286. `siteDocs`, grouped by connector, biggest group first. */
export function siteDocGroups(mine: MineCode, ctx: SiteSceneCtx): SiteDocGroup[] {
  const groups = new Map<string, LakeIndexEntry[]>();
  for (const entry of siteDocs(mine, ctx)) {
    const bucket = groups.get(entry.conn) ?? [];
    bucket.push(entry);
    groups.set(entry.conn, bucket);
  }
  return [...groups.entries()]
    .map(([connector, entries]) => ({ connector, entries }))
    .sort((a, b) => b.entries.length - a.entries.length);
}

// ---------------------------------------------------------------------------
// Open work
// ---------------------------------------------------------------------------

/**
 * Demo line 3280. The open alerts on machines at this site, as the Issues
 * surface's card data — the window, the cost of waiting and the chain.
 */
export const siteAlertCards = (mine: MineCode, ctx: SiteSceneCtx): AlertCardData[] =>
  ALERTS.filter((a) => BY_ID[a.machine]?.mine === mine).map((a) => alertCardData(a, ctx));

/** One closed-incident card on the Open-work tab. */
export interface SiteIncidentCard {
  readonly id: string;
  readonly date: string;
  readonly title: string;
  /** `'A$184,200 as it happened against A$21,400 replayed'`. */
  readonly costLine: string;
  readonly href: string;
}

/**
 * Demo line 3282. The closed incidents at this site. Costs come from
 * `incidentStrip()`, which derives the work order the demo's way (`i.id` when
 * it starts `WO`, else the second half of `i.ref`) and prices it all-in.
 */
export function siteIncidentCards(mine: MineCode, ctx: SiteSceneCtx): SiteIncidentCard[] {
  return incidentStrip(ctx)
    .filter((c) => c.incident.mine === mine)
    .map((c) => ({
      id: c.incident.id,
      date: c.incident.date,
      title: c.incident.title,
      costLine: `${fmt$(c.actual)} as it happened against ${fmt$(c.planned)} replayed`,
      href: routeForView('incident', c.incident.id) ?? '/issues/incidents',
    }));
}

/** Demo line 3203. Open jobs on a site, for the Overview and Work tabs. */
export const openJobs = (mine: MineCode, ctx: SiteSceneCtx) => siteJobs(mine, ctx);
